import { isPositiveNumber, reduce } from "et3-type-guards";
import { camelCase, startCase } from "lodash";

import i18n from "../../../../../constants/i18n";
import ET3Div from "../../../../../core/et3-div";
import { withLiteObserverAndTheme } from "../../../../../core/et3-enhanced-render";
import Et3Text from "../../../../../core/et3-text";
import {
  SURFACE_BORDER,
  SURFACE_RADIUS_CSS,
  SURFACE_SHADOW
} from "../../../../../core/et3-theme/surface-constants";
import getCorePersonalStore from "../../../../../stores/core/core-personal-store";
import getUserAdvancedStore from "../../../../../stores/user-store/advanced-store";
import { getActivationLabelKey } from "../activation-labels";
import UserCounterIndex from "../user-counter-index";
import SummaryChip from "./SummaryChip";
import SummaryNavButtons from "./SummaryNavButtons";
import SummaryFact from "./SummaryFact";
import {
  SUMMARY_CHIP_SOFT_CRITICAL,
  SUMMARY_CHIP_SOFT_NEUTRAL,
  SUMMARY_CHIP_SOFT_POSITIVE,
  SUMMARY_CHIP_SOFT_WARNING,
  SUMMARY_FACTS_GAP_X,
  SUMMARY_FACTS_GAP_Y,
  SUMMARY_HEADER_ACCENT_WIDTH,
  SUMMARY_HEADER_IDENTITY_HEIGHT,
  SUMMARY_HEADER_ITEM_GAP,
  SUMMARY_HEADER_PADDING_X,
  SUMMARY_HEADER_PADDING_Y,
  SUMMARY_HEADER_ROW_GAP,
  SUMMARY_HEADER_WIDTH,
  SUMMARY_STICKY_TOP,
  SUMMARY_STICKY_Z_INDEX,
  SUMMARY_USERNAME_FONT_SIZE,
  SUMMARY_USER_ID_FONT_SIZE,
  SUMMARY_USER_ID_PADDING,
  SUMMARY_USER_ID_RADIUS_CSS
} from "./summary-header-constants";

const UserSummaryHeader = withLiteObserverAndTheme((props) => {
  const { theme } = props;
  const { colors, unitX, unitY } = theme;
  const advancedStore = getUserAdvancedStore();
  const userId = advancedStore.currentUserId.get();
  const data = advancedStore.userData.get(userId);
  const isAnyKhattabaAdmin = getCorePersonalStore().isAnyKhattabaAdmin.get();
  const { userIsUnderCsrInvestigation } =
    advancedStore.currentUserIsUnderCsrInvestigation.get();
  const currentUserIsUnderCsrReport =
    advancedStore.currentUserIsUnderCsrReport.get();

  const username = reduce(() => `${data.username}`, "");
  const status = reduce(() => `${data.status}`, "");
  const isActiveStatus = status === "active";
  const verificationStatus = reduce(() => `${data.verificationStatus}`, "");
  const numberOfVerifiedApprovedPictures = reduce(
    () => data.numberOfVerifiedApprovedPictures,
    0
  );
  const isSelfieVerified = isPositiveNumber(numberOfVerifiedApprovedPictures);

  return (
    <ET3Div
      isScrollable={false}
      isExtendable={true}
      withShadow={false}
      width={SUMMARY_HEADER_WIDTH}
      height={0}
      fDirection="row"
      alignItems="stretch"
      justifyContent="flex-start"
      color={colors.all.white}
      style={{
        boxSizing: "border-box",
        position: "sticky",
        top: SUMMARY_STICKY_TOP,
        zIndex: SUMMARY_STICKY_Z_INDEX,
        border: SURFACE_BORDER,
        borderRadius: SURFACE_RADIUS_CSS,
        boxShadow: SURFACE_SHADOW,
        overflow: "hidden"
      }}
      onClick={() => {}}
    >
      <ET3Div
        isScrollable={false}
        isExtendable={true}
        withShadow={false}
        width={SUMMARY_HEADER_ACCENT_WIDTH}
        height={0}
        fDirection="column"
        alignItems="stretch"
        justifyContent="flex-start"
        color={isActiveStatus ? colors.all.green : colors.all.red}
        style={{ flexShrink: 0 }}
        onClick={() => {}}
      />
      <ET3Div
        isScrollable={false}
        isExtendable={true}
        withShadow={false}
        width={0}
        height={0}
        fDirection="column"
        alignItems="stretch"
        justifyContent="flex-start"
        color={colors.all.transparent}
        style={{
          boxSizing: "border-box",
          flex: 1,
          minWidth: 0,
          padding: `${SUMMARY_HEADER_PADDING_Y * unitY}px ${
            SUMMARY_HEADER_PADDING_X * unitX
          }px`,
          gap: SUMMARY_HEADER_ROW_GAP * unitY
        }}
        onClick={() => {}}
      >
        <ET3Div
          isScrollable={false}
          isExtendable={true}
          withShadow={false}
          width={0}
          height={SUMMARY_HEADER_IDENTITY_HEIGHT}
          fDirection="row"
          alignItems="center"
          justifyContent="flex-start"
          color={colors.all.transparent}
          style={{
            width: "100%",
            flexWrap: "wrap",
            gap: SUMMARY_HEADER_ITEM_GAP * unitX
          }}
          onClick={() => {}}
        >
          <Et3Text
            fontFamily={"BOLD"}
            fontSize={SUMMARY_USERNAME_FONT_SIZE}
            text={username}
            isSpan={true}
            color={colors.all.maastrichtBlue}
            style={{ margin: 0 }}
          />
          <ET3Div
            isScrollable={false}
            isExtendable={false}
            withShadow={false}
            width={0}
            height={0}
            fDirection="row"
            alignItems="center"
            justifyContent="center"
            color={colors.all.paleGrayishWhite}
            style={{
              boxSizing: "border-box",
              width: "auto",
              height: "auto",
              minHeight: 0,
              flexShrink: 0,
              border: SURFACE_BORDER,
              borderRadius: SUMMARY_USER_ID_RADIUS_CSS,
              paddingLeft: SUMMARY_USER_ID_PADDING * unitX,
              paddingRight: SUMMARY_USER_ID_PADDING * unitX
            }}
            onClick={() => {}}
          >
            <Et3Text
              fontFamily={"MEDIUM"}
              fontSize={SUMMARY_USER_ID_FONT_SIZE}
              text={`#${userId}`}
              isSpan={true}
              color={colors.all.davysGrey}
              style={{ margin: 0 }}
            />
          </ET3Div>
          <SummaryChip
            label={startCase(camelCase(status))}
            toneColor={isActiveStatus ? colors.all.green : colors.all.red}
            backgroundColor={
              isActiveStatus
                ? SUMMARY_CHIP_SOFT_POSITIVE
                : SUMMARY_CHIP_SOFT_CRITICAL
            }
          />
          {userIsUnderCsrInvestigation && !isAnyKhattabaAdmin ? (
            <SummaryChip
              label={i18n.get("UNDER_CSR_INVESTIGATION_STATUS")}
              toneColor={colors.all.darkOrange}
              backgroundColor={SUMMARY_CHIP_SOFT_WARNING}
            />
          ) : null}
          {currentUserIsUnderCsrReport && !isAnyKhattabaAdmin ? (
            <SummaryChip
              label={i18n.get("REPORTED")}
              toneColor={colors.all.blue}
              backgroundColor={SUMMARY_CHIP_SOFT_NEUTRAL}
            />
          ) : null}
          <UserCounterIndex />
          <SummaryNavButtons />
        </ET3Div>
        <ET3Div
          isScrollable={false}
          isExtendable={true}
          withShadow={false}
          width={0}
          height={0}
          fDirection="row"
          alignItems="center"
          justifyContent="flex-start"
          color={colors.all.transparent}
          style={{
            width: "100%",
            flexWrap: "wrap",
            gap: `${SUMMARY_FACTS_GAP_Y * unitY}px ${
              SUMMARY_FACTS_GAP_X * unitX
            }px`
          }}
          onClick={() => {}}
        >
          <SummaryFact
            labelKey={"CREATED_ON"}
            value={reduce(() => `${data.createdOn} · ${data.createdFrom}`, "")}
          />
          <SummaryFact
            labelKey={"LAST_LOGIN"}
            value={reduce(() => `${data.lastLogin}`, "")}
          />
          <SummaryFact
            labelKey={"CITY_NAME"}
            value={reduce(
              () => `${data.cityName}, ${data.residenceCountry}`,
              ""
            )}
          />
          <SummaryFact
            labelKey={"PLATFORM"}
            value={reduce(
              () => `${data.mobileAppPlatform} ${data.mobileAppVersion}`,
              ""
            )}
          />
          <SummaryFact
            labelKey={"ACTIVATION_STATUS"}
            value={i18n.get(getActivationLabelKey(verificationStatus))}
          />
          <SummaryFact
            labelKey={"SELFIE_VERIFICATION"}
            value={
              isSelfieVerified
                ? `${i18n.get("VERIFIED_BADGE")} · ${numberOfVerifiedApprovedPictures}`
                : i18n.get("NOT_VERIFIED_BADGE")
            }
          />
          <SummaryFact
            labelKey={"LOGIN_COUNT"}
            value={reduce(() => `${data.loginCount}`, "")}
          />
        </ET3Div>
      </ET3Div>
    </ET3Div>
  );
});
export default UserSummaryHeader;
